// Module-level registry of live capture clients, so the logout flow can
// reach every mounted client and flush its in-memory coalesce BEFORE the
// held-count guard reads the device (issue #726).
//
// The gap this closes: a capture `+`/`-` tap first sits in the PowerSync
// capture client's in-memory 500 ms coalesce map (capture-client-
// powersync.ts) BEFORE it is written to local SQLite / the ps_crud upload
// queue. The logout guard `getHeldCaptureLoad()` (logout-cleanup.ts) reads
// only ps_crud + orphaned_capture_items — never the coalesce map. A
// tap-then-sign-out inside the debounce window therefore reads {0,0}, shows
// no warning, and `disconnectAndClear({ clearLocal: true })` destroys the
// not-yet-persisted contractual count SILENTLY. Field staff capture on
// shared devices in production, so this is a live data-loss path.
//
// The capture client is created PER-COMPONENT (`useMemo(() =>
// createSelectedCaptureClient())` on each capture screen) with no global
// handle the auth provider can reach. This module is that handle: each
// capture screen registers its client on mount and unregisters on unmount,
// and `auth-provider.tsx :: logout()` calls `flushAllCaptureClients()` —
// bounded — before `getHeldCaptureLoad()`. Once flushed, every coalesced
// tap is persisted to ps_crud, so the EXISTING guard read surfaces it; the
// held-total contract (`HeldCaptureLoad` / `getHeldCaptureLoad`) is
// untouched.
//
// Module scope (not an instance field) because there is no single owner: the
// emitters are independent screen components and the sole consumer (the
// logout flow) has no reference to any of them. Mirrors the `_reset*`
// pub/sub pattern in hooks/powersync/upload-events.ts.
//
// Deliberately MINIMAL: it exposes register / unregister / flushAll only. It
// does NOT surface a `pendingCount()`-as-dialog-total — the dialog total
// stays sourced from `getHeldCaptureLoad()` (the ps_crud + quarantine read)
// after the flush, so the held-total contract has exactly one source of
// truth.

/**
 * The subset of the capture-client surface this registry depends on. Only
 * `flush()` is needed — the registry's single job is to drain every live
 * client's in-memory coalesce into durable storage on demand. Typed as its
 * own interface (rather than importing the full client type) so the registry
 * has no dependency on the adapter internals.
 */
export interface FlushableCaptureClient {
  /**
   * Resolve once every pending coalesced mutation has been written to local
   * SQLite / the ps_crud upload queue. Must not reject — see
   * `flushAllCaptureClients`.
   */
  flush(): Promise<void>
}

/**
 * Live capture clients. A Set so double-register (e.g. a React StrictMode
 * double-invoke of the mount effect) collapses to one entry, and unregister
 * is idempotent.
 */
const clients = new Set<FlushableCaptureClient>()

/**
 * In-flight unmount flushes from clients that have ALREADY unregistered (#810 P1).
 * A capture screen unregisters synchronously on unmount (use-registered-capture-
 * client.ts), but its `destroy()` coalesce flush is fire-and-forget and may still
 * be draining to ps_crud. The capture error-boundary Reload (#810) awaits
 * `flushAllCaptureClients()` AFTER the screen has unmounted, so without this the
 * awaited guarantee would see an empty `clients` set and reload while that write is
 * still pending — losing the tap. `retainPendingFlush` keeps the unmount flush
 * awaitable until it settles.
 */
const pendingFlushes = new Set<Promise<void>>()

/**
 * Retain an in-flight flush from an unregistering client so `flushAllCaptureClients`
 * still awaits it (#810 P1). Auto-removes on settle. The `then(,)` here swallows the
 * promise's OWN settled state so an unawaited rejected unmount flush (no logout /
 * error-boundary drain in flight) does not surface as an unhandled rejection; a
 * CONCURRENT `flushAllCaptureClients()` still observes the rejection via its own
 * await and fails closed.
 */
export function retainPendingFlush(flush: Promise<void>): void {
  pendingFlushes.add(flush)
  void flush.then(
    () => pendingFlushes.delete(flush),
    () => pendingFlushes.delete(flush),
  )
}

/**
 * Register a live capture client. Returns an unregister function (mirrors the
 * unsubscribe-fn pattern of the upload-events channels) so a mount effect can
 * `return registerCaptureClient(client)` for cleanup. Calling the returned
 * function is equivalent to `unregisterCaptureClient(client)` and is
 * idempotent.
 */
export function registerCaptureClient(
  client: FlushableCaptureClient,
): () => void {
  clients.add(client)
  return () => {
    clients.delete(client)
  }
}

/**
 * Unregister a live capture client. Idempotent — unregistering a client that
 * is not present is a no-op. Provided alongside the returned unregister fn so
 * callers can unregister by reference without retaining the closure.
 */
export function unregisterCaptureClient(client: FlushableCaptureClient): void {
  clients.delete(client)
}

/**
 * Flush every registered client's in-memory coalesce to durable storage.
 *
 * Resolves only once ALL clients have flushed. Iterates a snapshot so a
 * client that unregisters itself mid-flush (an unmount racing the logout)
 * cannot trip the Set iterator. Each client's `flush()` is expected to be
 * fail-closed on its own write errors (it surfaces a rejection rather than
 * swallowing a failed persist), and this function does NOT catch — a rejected
 * flush propagates so the logout preflight can FAIL CLOSED to the loud
 * held-data warning rather than fall through to the destructive clear with a
 * count still trapped in memory. See `auth-provider.tsx :: logout()`.
 *
 * Also awaits any retained in-flight unmount flushes (#810 P1) so the capture
 * error-boundary Reload, which runs after the throwing screen has unmounted and
 * unregistered, still waits for that screen's pending coalesce drain instead of
 * reloading over it.
 */
export async function flushAllCaptureClients(): Promise<void> {
  const snapshot = Array.from(clients)
  const pending = Array.from(pendingFlushes)
  await Promise.all([
    ...snapshot.map((client) => client.flush()),
    ...pending,
  ])
}

/**
 * Test helper — clear the registry. Tests register clients and MUST reset to
 * avoid cross-test leakage. Production code uses the unregister fn returned by
 * `registerCaptureClient`. Mirrors `_resetUploadSubscribers` in
 * hooks/powersync/upload-events.ts.
 */
export function _resetCaptureClientRegistry(): void {
  clients.clear()
  pendingFlushes.clear()
}
